/**
 * The rows of a paste preview: what the patch will ACTUALLY change on the draft it lands on (Option C C3 review
 * C3-06). A field shows when the string carried it, or when the patch changes it implicitly — e.g. the authentication
 * the patch always writes (Windows → SQL Server authentication), Oracle's Connect by, Azure Storage's Connect using and
 * resource scope. Values are made safe with the shared helpers; every secret is a fixed mask. Pure.
 */
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import {
  AZURE_STORAGE_AUTH_OPTIONS,
  STORAGE_SERVICE_LABELS,
  describeStorageResource,
  storageConnectMode,
  storageModeOnScreen,
  type StorageConnectMode,
} from '../../components/azureStorageFormRules';
import { sameValues } from '../state/engineDrafts';
import { loginLabel, secretLabel, targetLabel } from '../fields/fieldLabels';
import { redactSecretText, safeServerText } from '@/types/safeConnectionDisplay';
import type { StorageService } from '@/types/azureStorage';

type Values = Partial<ConnectionFormValues>;

/** Shown for every secret, whatever its length. */
export const SECRET_MASK = '••••••';

export type PasteRow = { field: string; label: string; value: string; secret?: true };

const onOff = (value: unknown) => (value ? 'On' : 'Off');
const STORAGE_MODES: Record<StorageConnectMode, string> = { account: 'Storage account', emulator: 'Emulator (Azurite)', resource: 'Single resource' };
const ORACLE_MODES: Record<string, string> = { ezconnect: 'EZConnect', tns_alias: 'TNS alias', tns_descriptor: 'TNS descriptor' };

function authLabel(engine: DatabaseEngine, auth: string): string {
  if (engine !== 'azurestorage' && engine !== 'kusto') return auth;
  return AZURE_STORAGE_AUTH_OPTIONS.find((option) => option.value === auth)?.label ?? auth;
}

/**
 * `patch` applied to `base` (the target draft before it), with `parsed` = what the string itself carried. `after` is
 * the draft after the patch.
 */
export function pasteRows(engine: DatabaseEngine, patch: Values, parsed: Values, base: Values, after: Values): PasteRow[] {
  const rows: PasteRow[] = [];
  const add = (field: string, label: string, value: string, secret?: true) => rows.push({ field, label, value, ...(secret ? { secret } : {}) });
  const text = (field: keyof Values) => String(patch[field] ?? '');
  // Shown when the string carried the field (and it is not blank), or when the patch changes the draft's value.
  const shows = (field: keyof Values) =>
    patch[field] !== undefined && ((parsed[field] !== undefined && text(field).trim() !== '') || !sameValues(patch[field], base[field]));

  if (engine === 'azurestorage') {
    const before = storageModeOnScreen(base);
    const mode = storageConnectMode(after);
    if (patch.azureStorageEmulator === true || mode !== before) add('azureStorageMode', 'Connect using', STORAGE_MODES[mode]);
  }
  if (engine === 'oracle' && shows('oracleConnectionStringType') && !sameValues(patch.oracleConnectionStringType, base.oracleConnectionStringType ?? 'ezconnect')) {
    add('oracleConnectionStringType', 'Connect by', ORACLE_MODES[text('oracleConnectionStringType')] ?? text('oracleConnectionStringType'));
  }
  if (shows('server') && text('server').trim()) add('server', targetLabel(after), safeServerText(text('server'), engine));
  if (patch.azureStorageScope?.kind === 'resource' && (parsed.azureStorageScope !== undefined || !sameValues(patch.azureStorageScope, base.azureStorageScope))) {
    add('azureStorageScope', 'Resource', redactSecretText(describeStorageResource(patch.azureStorageScope.resource)));
  }
  if (engine !== 'sqlite' && engine !== 'azurestorage' && engine !== 'kusto' && shows('port')) add('port', 'Port', redactSecretText(text('port')));
  if (shows('oracleServiceName')) add('oracleServiceName', patch.oracleUseSID ? 'SID' : 'Service name', redactSecretText(text('oracleServiceName')));
  if (shows('database') && text('database').trim()) add('database', 'Database', redactSecretText(text('database')));
  if (shows('authentication')) add('authentication', 'Authentication', authLabel(engine, text('authentication')));
  if (shows('login') && text('login').trim()) add('login', loginLabel(engine), redactSecretText(text('login')));
  if (patch.password !== undefined && text('password')) add('password', secretLabel(engine, after.authentication), SECRET_MASK, true);
  if (shows('entraTenantId') && text('entraTenantId').trim()) add('entraTenantId', 'Tenant ID', redactSecretText(text('entraTenantId')));
  if (shows('encrypt')) add('encrypt', 'Encryption', onOff(patch.encrypt));
  if (shows('trustServerCertificate')) add('trustServerCertificate', 'Trust server certificate', patch.trustServerCertificate ? 'Yes' : 'No');
  if (shows('ssl')) add('ssl', 'SSL', onOff(patch.ssl));
  // Each endpoint the patch writes, by value (masked): an override URL is where the connection will go.
  for (const [service, endpoint] of Object.entries(patch.azureStorageEndpoints ?? {})) {
    if (!endpoint || (parsed.azureStorageEndpoints === undefined && sameValues(endpoint, base.azureStorageEndpoints?.[service as keyof typeof base.azureStorageEndpoints]))) continue;
    add(`azureStorageEndpoints.${service}`, `${STORAGE_SERVICE_LABELS[service as StorageService] ?? service} endpoint`, safeServerText(String(endpoint)));
  }
  if (patch.azureStorageServices && shows('azureStorageServices')) {
    add('azureStorageServices', 'Services', patch.azureStorageServices.map((service) => STORAGE_SERVICE_LABELS[service] ?? service).join(', '));
  }
  return rows;
}
